import { Tabs } from "@mantine/core";
import type { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { PanelErrorFallback } from "@/panels/modules/panels/components/PanelErrorFallback";
import type { MainPanelConfig, TabOption } from "@/panels/modules/panels/store/panels.store.types";
import styles from "./MainPanelContent.module.css";
import { MainPanelTabContent } from "./MainPanelTabContent";

interface TabsContainerProps {
    mainPanelConfig: MainPanelConfig;
    showTabBar: boolean;
    onTabChange: (tab: TabOption) => void;
    onCloseTab: (tab: TabOption) => void;
    onMinimizeTab: (tab: TabOption) => void;
    tabASlot?: ReactNode;
    tabBSlot?: ReactNode;
    showHeader: boolean;
}

export const MainPanelContent = ({
    mainPanelConfig,
    showTabBar,
    onTabChange,
    onCloseTab,
    onMinimizeTab,
    tabASlot,
    tabBSlot,
    showHeader,
}: TabsContainerProps) => {
    const { selectedTab, a, b } = mainPanelConfig;

    const handleTabChange = (tab: string | null) => {
        if (!tab) return;
        onTabChange(tab as TabOption);
    };

    const handleCloseTab = (tab: TabOption) => () => {
        onCloseTab(tab);
    };

    const handleMinimizeTab = (tab: TabOption) => () => {
        onMinimizeTab(tab);
    };

    return (
        <Tabs variant={"unstyled"} value={selectedTab} classNames={styles} onChange={handleTabChange}>
            {showTabBar && (
                <Tabs.List>
                    <Tabs.Tab value="a">Tab A</Tabs.Tab>
                    <Tabs.Tab value="b">Tab B</Tabs.Tab>
                </Tabs.List>
            )}

            <Tabs.Panel value="a" className={styles.tabPanel}>
                <MainPanelTabContent
                    title={a.title}
                    isMinimized={a.isMinimized}
                    isClosed={a.isClosed}
                    onClose={handleCloseTab("a")}
                    onMinimize={handleMinimizeTab("a")}
                    showHeader={showHeader}
                >
                    <ErrorBoundary fallbackRender={PanelErrorFallback}>{tabASlot}</ErrorBoundary>
                </MainPanelTabContent>
            </Tabs.Panel>

            <Tabs.Panel value="b" className={styles.tabPanel}>
                <MainPanelTabContent
                    title={b.title}
                    isMinimized={b.isMinimized}
                    isClosed={b.isClosed}
                    onClose={handleCloseTab("b")}
                    onMinimize={handleMinimizeTab("b")}
                    showHeader={showHeader}
                >
                    <ErrorBoundary fallbackRender={PanelErrorFallback}>{tabBSlot}</ErrorBoundary>
                </MainPanelTabContent>
            </Tabs.Panel>
        </Tabs>
    );
};
